import { ExtractedPhotoRecipe } from '../../types/photoImport';
import { CANONICAL_DISH_TYPES } from '../../utils/dishTypeVocabulary';
import { recipeLanguageField } from '../../utils/languageDetection';

export interface BuildPhotoRecipeDocParams {
  documentId: string;
  userId: string;
  confidence: number;
  /** Persistent image URL (e.g. Firebase Storage download URL). Empty string when none. */
  imageUrl?: string;
  /** The source line the recipe shows, in the cook's language at import. */
  sourceName?: string;
  /** Injectable timestamp for deterministic tests; defaults to now. */
  savedAt?: string;
}

/**
 * Builds the Firestore document for a photo-imported recipe.
 *
 * Writes the SAME structured shape the web scraper produces
 * (`extendedIngredients` objects + `instructions` as `{ number, step }`), so the
 * recipe-detail screen renders ingredients/instructions correctly. The plain
 * `ingredients` string array is kept for back-compat (search index, etc.).
 *
 * Pure function — no I/O — so it can be unit-tested directly.
 */
export function buildPhotoRecipeDoc(
  recipe: ExtractedPhotoRecipe,
  { documentId, userId, confidence, imageUrl = '', savedAt, sourceName = 'Photo import' }: BuildPhotoRecipeDocParams,
) {
  const prepTimeStr = recipe.prepTime ? `${recipe.prepTime} minutes` : '';
  const cookTimeStr = recipe.cookTime ? `${recipe.cookTime} minutes` : '';

  // The app reads the NUMERIC fields (readyInMinutes drives the recipe index,
  // list display, and quality checks) — the string fields above are legacy.
  // Without this, an extracted time was saved but never shown ("Temps manquant").
  const readyInMinutes =
    recipe.totalTime || (recipe.prepTime ?? 0) + (recipe.cookTime ?? 0);

  const ingredientStrings = recipe.ingredients.map((ing) => `${ing.quantity} ${ing.name}`.trim());

  const extendedIngredients = recipe.ingredients.map((ing, idx) => ({
    id: idx,
    name: ing.name,
    original: `${ing.quantity} ${ing.name}`.trim(),
    amount: 0,
    unit: '',
  }));

  const steps = recipe.instructions.map((inst, idx) => ({
    number: inst.step || idx + 1,
    step: inst.text,
  }));

  // Only accept a canonical dish type — anything else stays unclassified so
  // the quality checker surfaces it instead of silently mislabeling.
  const dishTypes = (CANONICAL_DISH_TYPES as readonly string[]).includes(recipe.dishType ?? '')
    ? [recipe.dishType as string]
    : [];

  return {
    id: documentId,
    title: recipe.title,
    description: '',
    image: imageUrl,
    prepTime: prepTimeStr,
    cookTime: cookTimeStr,
    totalTime: recipe.totalTime ? `${recipe.totalTime} minutes` : '',
    readyInMinutes: readyInMinutes > 0 ? readyInMinutes : 0,
    preparationMinutes: recipe.prepTime ?? 0,
    cookingMinutes: recipe.cookTime ?? 0,
    recipeYield: recipe.servings || '4',
    recipeCategory: dishTypes[0] || '',
    dishTypes,
    recipeCuisine: recipe.cuisine || '',
    // Without it the planner and the grocery list never translate the recipe.
    ...recipeLanguageField([recipe.title, ...ingredientStrings, ...steps.map(s => s.step)]),
    ingredients: ingredientStrings,
    extendedIngredients,
    instructions: steps,
    analyzedInstructions: [{ name: '', steps }],
    author: '',
    keywords: '',
    nutrition: null,
    diets: [],
    sourceUrl: '',
    sourceName,
    savedAt: savedAt ?? new Date().toISOString(),
    source: 'photo-import' as const,
    type: 'imported' as const,
    userId,
    photoConfidence: confidence,
  };
}
